<template>
    <div class="fu-page">
        <!-- header -->
        <div class="fu-head">
            <div class="fu-title-wrap">
                <span class="fu-title-ic"><v-icon small>mdi-clipboard-text-clock-outline</v-icon></span>
                <h1 class="fu-title">{{ $t('ProcessFollowup') }}</h1>
                <span v-if="loadGrid" class="fu-count">{{ totalCount }}</span>
            </div>
        </div>

        <!-- filters -->
        <v-card flat class="fu-card fu-filters mb-4" :loading="loading">
            <div class="fu-filters-grid">
                <div class="fu-field">
                    <label class="fu-label">{{ $t('ProcessType') }}</label>
                    <v-select v-model="processTypeId" :loading="controlLoading"
                              :items="processTypes"
                              clearable dense outlined hide-details
                              :menu-props="{ offsetY: true, rounded: 'lg', contentClass: 'hdr-menu' }"
                              item-text="name"
                              item-value="id" />
                </div>
                <div class="fu-field">
                    <label class="fu-label">{{ $t('Status') }}</label>
                    <v-select v-model="status" :loading="controlLoading"
                              :items="statuses"
                              clearable dense outlined hide-details
                              :menu-props="{ offsetY: true, rounded: 'lg', contentClass: 'hdr-menu' }"
                              :item-text="item => $t(item.name)"
                              item-value="id">
                        <template v-slot:item="{ item, attrs, on }">
                            <v-list-item class="hdr-item" v-bind="attrs" v-on="on">
                                <v-list-item-title class="hdr-item-title">{{ $t(item.name) }}</v-list-item-title>
                            </v-list-item>
                        </template>
                    </v-select>
                </div>
                <div class="fu-field">
                    <label class="fu-label">{{ $t('ProcessTitle') }}</label>
                    <v-text-field v-model="title" clearable dense outlined hide-details maxlength="100" prepend-inner-icon="mdi-magnify" @keydown.enter.prevent="listResults()" />
                </div>
                <div class="fu-field">
                    <label class="fu-label">{{ `${$t('ReferenceNumber')}/${$t('ProcessInstanceId')}` }}</label>
                    <v-text-field v-model="processIdentifier" clearable dense outlined hide-details maxlength="20" prepend-inner-icon="mdi-pound" @keydown.enter.prevent="listResults()" />
                </div>
            </div>
            <div class="fu-filters-actions">
                <v-btn :disabled="loading" class="fu-btn fu-btn--primary" depressed color="primary" type="submit" @click="listResults()">
                    <v-icon small class="me-1">mdi-magnify</v-icon>{{ $t('Search') }}
                </v-btn>
                <v-btn :disabled="loading" class="fu-btn" outlined color="primary" @click="reset()">
                    <v-icon small class="me-1">mdi-restore</v-icon>{{ $t('Reset') }}
                </v-btn>
            </div>
        </v-card>

        <!-- results -->
        <v-card v-if="loadGrid" flat class="fu-card fu-grid">
            <GridWithServerPaging :headers="headers" :items="result" :total="totalCount" :loading="loading" disable-sort show-expand @onChange="listResults" @item-expanded="expand">
                <template v-slot:[`item.id`]="{ item }">
                    <div class="fu-id-cell">
                        <router-link class="fu-id" :to="{ name: 'followup-details', params: { processInstanceId: item.id } }">{{ item.id }}</router-link>
                        <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                                <router-link class="fu-action" :to="{ name: 'followup-details', params: { processInstanceId: item.id }}" target="_blank" v-bind="attrs" v-on="on" :aria-label="$t('OpenNewWindow')">
                                    <v-icon small>mdi-open-in-new</v-icon>
                                </router-link>
                            </template>
                            <span>{{ $t('OpenNewWindow') }}</span>
                        </v-tooltip>
                    </div>
                </template>
                <template v-slot:[`item.title`]="{ item }">
                    <span class="fu-row-title">{{ item.title }}</span>
                </template>
                <template v-slot:[`item.dateStarted`]="{ item }">
                    <span class="fu-date">{{ item.dateStarted | formatDateGregorian }}</span>
                </template>
                <template v-slot:[`item.status`]="{ item }">
                    <div class="fu-status-cell">
                        <span class="fu-status" :class="'fu-status--' + String(item.status || '').toLowerCase()">
                            <v-icon x-small class="fu-status-ic">{{ statusIcon(item.status) }}</v-icon>{{ $t(item.status) }}
                        </span>
                        <v-tooltip v-if="canViewComments(item)" bottom>
                            <template v-slot:activator="{ on, attrs }">
                                <button type="button" class="fu-action" v-bind="attrs" v-on="on" :aria-label="$t('ViewComments')" @click="openComments(item)">
                                    <v-icon small>mdi-comment-text-multiple-outline</v-icon>
                                </button>
                            </template>
                            <span>{{ $t('ViewComments') }}</span>
                        </v-tooltip>
                    </div>
                </template>
                <template v-slot:[`item.reference`]="{ item }">
                    <span class="fu-ref">{{ item.reference }}</span>
                </template>
                <template v-slot:expanded-item="{ headers, item }">
                    <td :colspan="headers.length" class="fu-expand">
                        <v-progress-linear v-if="loadingActivities" indeterminate color="primary" height="2" />
                        <div v-if="item.activities && item.activities.length > 0" class="fu-activities">
                            <table class="fu-act-table">
                                <thead>
                                    <tr>
                                        <th>{{ $t('Title') }}</th>
                                        <th>{{ $t('StartDate') }}</th>
                                        <th>{{ $t('DueDate') }}</th>
                                        <th>{{ $t('EndDate') }}</th>
                                        <th>{{ $t('TaskOwner') }}</th>
                                        <th>{{ $t('Status') }}</th>
                                        <th>{{ $t('ActivityNumber') }}</th>
                                        <th>{{ $t('ParentActivityNumber') }}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(activity, i) in item.activities" :key="i">
                                        <td class="fu-act-title">{{ activity.title }}</td>
                                        <td class="fu-date">{{ activity.startDate | formatDateTimeGregorian }}</td>
                                        <td class="fu-date">{{ activity.dueDate | formatDateGregorian }}</td>
                                        <td class="fu-date">{{ activity.endDate | formatDateTimeGregorian }}</td>
                                        <td>{{ activity.owner }}</td>
                                        <td><span class="fu-status fu-status--sm" :class="'fu-status--' + String(activity.status || '').toLowerCase()">{{ $t(activity.status) }}</span></td>
                                        <td class="fu-date">{{ activity.id }}</td>
                                        <td class="fu-date">{{ activity.parentId || '—' }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <div v-else-if="!loadingActivities" class="fu-empty-inline">{{ $t('NoItemsToShow') }}</div>
                    </td>
                </template>
            </GridWithServerPaging>
        </v-card>

        <!-- comments / completion notes viewer (shared component: shows the action badge) -->
        <CommentsDialog v-model="commentsDialog"
                        :comments="comments"
                        :loading="commentsLoading"
                        :reference="commentsRef" />
    </div>
</template>

<script>
    import ProcessesService from '@/services/ProcessesService.js'
    import LookupsService from '../../services/LookupsService'
    import GridWithServerPaging from '@/components/controls/GridWithServerPaging.vue'
    import CommentsDialog from '@/components/controls/CommentsDialog.vue'
    import { mapGetters } from 'vuex'

    export default {
        components: {
            GridWithServerPaging,
            CommentsDialog
        },

        data() {
            return {
                loadingActivities: false,
                loading: false,
                loadGrid: false,
                controlLoading: false,
                status: null,
                processTypeId: null,
                processTypes: [],
                title: null,
                totalCount: 0,
                processIdentifier: null,
                searchText: '',
                statuses: [],
                result: [],
                commentsDialog: false,
                commentsLoading: false,
                comments: [],
                commentsRef: '',
                headers: [
                    { text: this.$t('ProcessInstanceId'), align: 'start', value: 'id', sortable: false },
                    { text: this.$t('Title'), value: 'title', sortable: false },
                    { text: this.$t('StartDate'), value: 'dateStarted', sortable: false },
                    { text: this.$t('Status'), value: 'status', sortable: false },
                    { text: this.$t('ReferenceNumber'), value: 'reference', sortable: false },
                ],
            }
        },

        created() {
            this.listProcessStatuses()
            this.listProcessTypes()
        },
        computed: {
            ...mapGetters('user', ['loggedInUser', 'language']),
            isArabicLanguage() {
                return this.language === 'ar'
            },
        },
        methods: {
            listProcessStatuses() {
                this.controlLoading = true
                LookupsService.listProcessStatuses().then((res) => {
                    if (res.data) {
                        this.statuses = res.data
                    }
                }).catch(() => {
                    this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                }).finally(() => {
                    this.controlLoading = false
                })
            },

            listProcessTypes() {
                LookupsService.listProcessTypes().then((res) => {
                    if (res.data) {
                        this.processTypes = res.data
                    }
                }).catch(() => {
                    this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                })
            },

            listResults(options) {
                this.loadGrid = true
                this.loading = true

                const searchObj = {
                    text: this.searchText || null,
                    statusId: this.status,
                    processTypeId: this.processTypeId || null,
                    title: this.title || null,
                    processIdentifier: this.processIdentifier || null,
                    page: options != undefined ? options.page : 1,
                    pageSize: options != undefined ? options.itemsPerPage : 10,
                    sortBy: options != undefined && options.sortBy && options.sortBy.length > 0 ? options.sortBy[0] : null,
                    sortDesc: options != undefined && options.sortDesc && options.sortDesc.length > 0 ? options.sortDesc[0] : null,
                }

                ProcessesService.list(searchObj)
                    .then((res) => {
                        this.result = res.data.data
                        this.totalCount = res.data.total
                        this.loading = false
                    })
                    .catch(() => {
                        this.result = []
                        this.totalCount = 0
                        this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                        this.loading = false
                    })
            },

            expand(data) {
                if (data.value == true && !data.item.activities) {
                    this.loadingActivities = true
                    ProcessesService.listActivities(data.item.id).then(res => {
                        if (res.data) {
                            data.item.activities = res.data
                        }
                        else {
                            this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                        }
                    }).catch(() => {
                        this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                    }).finally(() => {
                        this.loadingActivities = false
                    })
                }
            },

            reset() {
                this.status = null
                this.processTypeId = null
                this.processIdentifier = null
                this.result = []
                this.totalCount = 0
                this.searchText = null
                this.title = null
            },

            statusColor(status) {
                const map = { Completed: '#1B8354', InProgress: '#f59e0b', Active: '#f59e0b', Draft: '#90a4ae', Canceled: '#e53935', Cancelled: '#e53935' }
                return map[status] || '#607d8b'
            },
            statusIcon(status) {
                const map = {
                    Completed: 'mdi-check-circle', InProgress: 'mdi-progress-clock', Active: 'mdi-progress-clock',
                    Draft: 'mdi-pencil-outline', Canceled: 'mdi-close-circle', Cancelled: 'mdi-close-circle'
                }
                return map[status] || 'mdi-circle-medium'
            },

            // comment icon only for the 7 request workflows (ProcessType 2-8) that are completed AND have comments
            canViewComments(item) {
                return item.hasComments && item.status === 'Completed' && item.processTypeId >= 2 && item.processTypeId <= 8
            },
            openComments(item) {
                this.commentsRef = item.reference || ''
                this.comments = []
                this.commentsLoading = true
                this.commentsDialog = true
                ProcessesService.getProcessComments(item.id).then(res => {
                    this.comments = res.data || []
                }).catch(() => {
                    this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                }).finally(() => { this.commentsLoading = false })
            },
        },
    }
</script>

<style>
    /* Process follow-up page (global stylesheet: every rule is namespaced with .fu-) */
    .fu-page {
        --fu-surface: #f3f6f5;
        --fu-line: #e6ecea;
        --fu-text: #1f2a28;
        --fu-muted: #6b7a77;
        --fu-primary: #0a716e;
        --fu-primary-tint: #e6f2f1;
        --fu-amber: #b45309;
        --fu-amber-tint: #fdf3e7;
        --fu-danger: #b91c1c;
        --fu-danger-tint: #fee2e2;
        --fu-ease: cubic-bezier(.2, .8, .2, 1);
        padding: 22px 20px 32px;
        background: #fff;
    }

    /* ---------- header ---------- */
    .fu-head { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 14px; margin-bottom: 16px; }
    .fu-title-wrap { display: flex; align-items: center; gap: 12px; }
    .fu-title-ic {
        display: inline-flex; align-items: center; justify-content: center;
        width: 42px; height: 42px; border-radius: 13px; background: var(--fu-primary-tint);
    }
    .fu-title-ic .v-icon { color: var(--fu-primary) !important; }
    .fu-title { margin: 0; font-size: 22px; font-weight: 800; line-height: 1.2; color: var(--fu-text); }
    .fu-count {
        display: inline-flex; align-items: center; justify-content: center;
        min-width: 30px; height: 26px; padding: 0 9px; border-radius: 999px;
        background: var(--fu-primary); color: #fff; font-size: 12.5px; font-weight: 700;
    }

    /* ---------- cards ---------- */
    .fu-card {
        border: 1px solid var(--fu-line) !important;
        border-radius: 18px !important;
        box-shadow: 0 1px 2px rgba(15, 23, 42, .04), 0 8px 24px -16px rgba(15, 23, 42, .18) !important;
        overflow: hidden;
    }

    /* ---------- filters ---------- */
    .fu-filters { padding: 18px 18px 14px; }
    .fu-filters-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px; }
    .fu-field { display: flex; flex-direction: column; gap: 6px; min-width: 0; }
    .fu-label { font-size: 12px; font-weight: 700; color: var(--fu-muted); }
    .fu-field .v-input__slot {
        min-height: 40px !important; border-radius: 12px !important; background: var(--fu-surface);
        box-shadow: none !important;
    }
    .fu-field fieldset { border-color: var(--fu-line) !important; }
    .fu-field .v-input--is-focused fieldset { border-color: var(--fu-primary) !important; }
    .fu-field .v-input--is-focused .v-input__slot { background: #fff; }
    .fu-field input, .fu-field .v-select__selection { font-size: 13.5px; color: var(--fu-text); }
    .fu-field .v-input__prepend-inner .v-icon, .fu-field .v-input__append-inner .v-icon { color: var(--fu-muted) !important; }
    .fu-filters-actions { display: flex; gap: 8px; margin-top: 14px; }
    .fu-btn {
        border-radius: 12px !important; height: 40px !important; padding: 0 18px !important;
        font-weight: 700; letter-spacing: 0; text-transform: none;
    }
    .fu-btn--primary { box-shadow: 0 8px 18px -10px var(--fu-primary) !important; }

    /* ---------- results table ---------- */
    .fu-grid .v-data-table thead th {
        height: 46px !important; font-size: 12px !important; font-weight: 700 !important;
        color: var(--fu-muted) !important; background: #fafcfb !important;
        border-bottom: 1px solid var(--fu-line) !important; white-space: nowrap;
    }
    .fu-grid .v-data-table tbody td {
        height: 54px !important; font-size: 13.5px; color: var(--fu-text);
        border-bottom: 1px solid #eef2f1 !important;
    }
    .fu-grid .v-data-table tbody tr { transition: background-color .15s var(--fu-ease); }
    .fu-grid .v-data-table tbody tr:hover { background: var(--fu-surface) !important; }
    .fu-grid .v-data-table tbody tr.v-data-table__expanded__row { background: var(--fu-primary-tint) !important; }
    .fu-grid .v-data-table .v-data-footer { border-top: 1px solid var(--fu-line); font-size: 12.5px; color: var(--fu-muted); padding: 4px 8px; }
    .fu-grid .v-data-table__expand-icon { color: var(--fu-muted) !important; }

    .fu-id-cell { display: inline-flex; align-items: center; gap: 6px; }
    .fu-id {
        display: inline-block; padding: 3px 10px; border-radius: 8px;
        background: var(--fu-surface); color: var(--fu-text); font-size: 12.5px; font-weight: 700;
        font-variant-numeric: tabular-nums; text-decoration: none; transition: background-color .15s var(--fu-ease), color .15s var(--fu-ease);
    }
    .fu-id:hover { background: var(--fu-primary-tint); color: var(--fu-primary); }
    .fu-row-title { font-weight: 600; color: var(--fu-text); line-height: 1.45; }
    .fu-date { color: var(--fu-muted); font-size: 12.5px; font-variant-numeric: tabular-nums; white-space: nowrap; }
    .fu-ref {
        display: inline-block; padding: 3px 10px; border-radius: 999px;
        background: var(--fu-primary-tint); color: var(--fu-primary); font-size: 12px; font-weight: 700; white-space: nowrap;
    }

    .fu-status-cell { display: inline-flex; align-items: center; gap: 6px; }
    .fu-status {
        display: inline-flex; align-items: center; gap: 6px; height: 26px; padding: 0 10px;
        border-radius: 999px; font-size: 12px; font-weight: 700; white-space: nowrap;
        background: var(--fu-surface); color: var(--fu-muted);
    }
    .fu-status--sm { height: 22px; font-size: 11.5px; padding: 0 8px; }
    .fu-status .fu-status-ic { color: inherit !important; }
    .fu-status--completed { background: var(--fu-primary-tint); color: var(--fu-primary); }
    .fu-status--inprogress, .fu-status--active { background: var(--fu-amber-tint); color: var(--fu-amber); }
    .fu-status--draft { background: #eef1f0; color: #5c6b69; }
    .fu-status--canceled, .fu-status--cancelled { background: var(--fu-danger-tint); color: var(--fu-danger); }

    .fu-action {
        display: inline-flex; align-items: center; justify-content: center;
        width: 30px; height: 30px; border: 0; border-radius: 9px; background: transparent;
        text-decoration: none; cursor: pointer; padding: 0;
        transition: background-color .15s var(--fu-ease);
    }
    .fu-action .v-icon { color: var(--fu-muted) !important; transition: color .15s var(--fu-ease); }
    .fu-action:hover { background: var(--fu-primary-tint); }
    .fu-action:hover .v-icon { color: var(--fu-primary) !important; }
    .fu-action:focus { outline: none; }
    .fu-action:focus-visible { box-shadow: 0 0 0 2px #fff, 0 0 0 4px var(--fu-primary); }

    /* ---------- expanded activities ---------- */
    .fu-grid .v-data-table td.fu-expand { padding: 0 !important; background: #fafcfb; border-bottom: 1px solid var(--fu-line) !important; }
    .fu-activities { padding: 12px 16px 14px; }
    .fu-act-table { width: 100%; border-collapse: separate; border-spacing: 0; background: #fff; border: 1px solid var(--fu-line); border-radius: 12px; overflow: hidden; }
    .fu-act-table th {
        text-align: start; padding: 9px 12px; font-size: 11.5px; font-weight: 700; color: var(--fu-muted);
        background: var(--fu-surface); border-bottom: 1px solid var(--fu-line); white-space: nowrap;
    }
    .fu-act-table td { padding: 9px 12px; font-size: 12.5px; color: var(--fu-text); border-bottom: 1px solid #eef2f1; vertical-align: middle; }
    .fu-act-table tr:last-child td { border-bottom: 0; }
    .fu-act-title { font-weight: 600; }
    .fu-empty-inline { padding: 14px 16px; font-size: 13px; color: var(--fu-muted); }

    @media (max-width: 1264px) {
        .fu-filters-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    }
    @media (max-width: 600px) {
        .fu-page { padding: 14px 12px 24px; }
        .fu-title { font-size: 19px; }
        .fu-filters-grid { grid-template-columns: 1fr; }
    }
    @media (prefers-reduced-motion: reduce) {
        .fu-action, .fu-id, .fu-grid .v-data-table tbody tr { transition: none; }
    }
</style>
